import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShoppingCart, Mail, Phone, MapPin, FileText, ChevronDown, ChevronUp, Download, Search, Filter } from "lucide-react";

type OrderItem = {
  ksztalt?: string;
  szerokosc?: string | number;
  wysokosc?: string | number;
  ilosc?: string | number;
  folia?: string;
  fileUrl?: string;
  filePath?: string;
};

// Extract storage path from a (possibly outdated public) URL or use explicit filePath
const extractPath = (item: OrderItem): { bucket: string; path: string } | null => {
  if (item.filePath) return { bucket: "order-attachments", path: item.filePath };
  if (!item.fileUrl) return null;
  const m = item.fileUrl.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+?)(?:\?|$)/);
  if (!m) return null;
  return { bucket: m[1], path: decodeURIComponent(m[2]) };
};

const downloadFile = async (item: OrderItem) => {
  const info = extractPath(item);
  if (!info) {
    alert("Brak ścieżki pliku");
    return;
  }
  // Try order-attachments first, fallback to order-files
  const buckets = [info.bucket, info.bucket === "order-attachments" ? "order-files" : "order-attachments"];
  for (const bucket of buckets) {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(info.path, 3600, { download: true });
    if (data?.signedUrl && !error) {
      window.open(data.signedUrl, "_blank");
      return;
    }
  }
  alert("Nie udało się pobrać pliku. Sprawdź czy jesteś zalogowany jako admin.");
};

type Order = {
  id: string;
  created_at: string;
  source: string;
  name: string;
  email: string;
  phone: string | null;
  nip: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  order_number: string | null;
  notes: string | null;
  items: OrderItem[];
  status: string;
};

const statusColors: Record<string, string> = {
  nowe: "bg-blue-500/20 text-blue-400",
  "w realizacji": "bg-yellow-500/20 text-yellow-400",
  wysłane: "bg-green-500/20 text-green-400",
  anulowane: "bg-red-500/20 text-red-400",
};

const STATUSES = ["nowe", "w realizacji", "wysłane", "anulowane"];

const exportCSV = (orders: Order[]) => {
  const headers = ["Numer", "Data", "Imię", "Email", "Telefon", "NIP", "Ulica", "Kod", "Miasto", "Pozycje", "Status", "Uwagi"];
  const rows = orders.map((o) => {
    const items = Array.isArray(o.items) ? o.items : [];
    const itemsStr = items.map((it, i) => `${i + 1}. ${it.ksztalt || "-"} ${it.szerokosc || "-"}x${it.wysokosc || "-"}mm x${it.ilosc || "-"} (${it.folia || "-"})`).join(" | ");
    return [
      o.order_number || "",
      new Date(o.created_at).toLocaleDateString("pl-PL"),
      o.name,
      o.email,
      o.phone || "",
      o.nip || "",
      o.street || "",
      o.postal_code || "",
      o.city || "",
      itemsStr,
      o.status,
      o.notes || "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
  });

  const csv = "\uFEFF" + [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `zamowienia-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const OrdersList = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("wszystkie");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setOrders(data as unknown as Order[]);
    setLoading(false);
  };

  const updateStatus = async (id: string, status: string) => {
    await supabase.from("orders").update({ status }).eq("id", id);
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
  };

  const filtered = useMemo(() => {
    let result = orders;
    if (statusFilter !== "wszystkie") {
      result = result.filter((o) => o.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.email.toLowerCase().includes(q) ||
          (o.order_number && o.order_number.toLowerCase().includes(q)) ||
          (o.phone && o.phone.includes(q))
      );
    }
    return result;
  }, [orders, search, statusFilter]);

  if (loading) return <p className="text-muted-foreground text-sm">Ładowanie zamówień...</p>;

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-bold text-foreground">Zamówienia ({filtered.length})</h2>
        </div>
        <button
          onClick={() => exportCSV(filtered)}
          className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg border border-accent/40 text-accent hover:bg-accent/10 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Eksport CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Szukaj po nazwie, email, numerze..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          {["wszystkie", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
                statusFilter === s
                  ? "border-accent bg-accent/20 text-accent font-bold"
                  : "border-border text-muted-foreground hover:border-accent/50"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <p className="text-muted-foreground text-center py-8">Brak zamówień.</p>
      )}

      <div className="space-y-3">
        {filtered.map((order) => {
          const expanded = expandedId === order.id;
          const date = new Date(order.created_at);
          const dateStr = date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
          const items = Array.isArray(order.items) ? order.items : [];

          return (
            <div key={order.id} className="bg-background border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(expanded ? null : order.id)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-foreground">{order.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[order.status] || "bg-muted text-muted-foreground"}`}>
                      {order.status}
                    </span>
                    <span className="text-xs text-muted-foreground">{items.length} poz.</span>
                    {order.order_number && <span className="text-xs text-muted-foreground font-mono">{order.order_number}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{dateStr}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{order.email}</span>
                    {order.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{order.phone}</span>}
                  </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
              </button>

              {expanded && (
                <div className="border-t border-border p-4 space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground font-medium">Status:</span>
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(order.id, s)}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          order.status === s
                            ? "border-accent bg-accent/20 text-accent font-bold"
                            : "border-border text-muted-foreground hover:border-accent/50"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {order.nip && (
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-muted-foreground">NIP:</span>
                        <span className="text-foreground">{order.nip}</span>
                      </div>
                    )}
                    {(order.street || order.city) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-foreground">
                          {[order.street, order.postal_code, order.city].filter(Boolean).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  {items.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Pozycje</p>
                      {items.map((item, i) => (
                        <div key={i} className="bg-muted/30 rounded-lg p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div><span className="text-muted-foreground text-xs">Kształt:</span><br /><span className="text-foreground font-medium">{item.ksztalt || "—"}</span></div>
                          <div><span className="text-muted-foreground text-xs">Rozmiar:</span><br /><span className="text-foreground font-medium">{item.szerokosc || "—"} × {item.wysokosc || "—"} mm</span></div>
                          <div><span className="text-muted-foreground text-xs">Ilość:</span><br /><span className="text-foreground font-medium">{item.ilosc || "—"} szt.</span></div>
                          <div><span className="text-muted-foreground text-xs">Folia:</span><br /><span className="text-foreground font-medium">{item.folia || "—"}</span></div>
                          {(item.fileUrl || item.filePath) && (
                            <div className="col-span-full">
                              <button
                                onClick={() => downloadFile(item)}
                                className="inline-flex items-center gap-1 text-accent text-xs hover:underline font-semibold"
                              >
                                <Download className="w-3 h-3" /> Pobierz załącznik
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {order.notes && (
                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Uwagi</p>
                      <p className="text-sm text-foreground bg-muted/30 rounded-lg p-3">{order.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrdersList;
